import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    FormLabelProps,
    Input,
    List,
    ListItem,
    Portal,
    Spinner,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { InventoryTag } from '../../types/inventory';
import { useSearchInventoryTags } from '../../hooks/inventory/useSearchInventoryTags';
import { inventoryFieldProps } from '../../utils/inventoryFieldStyles';
import {
    draftInventoryTag,
    normalizeTagNameKey,
    tagListHasName,
} from '../../utils/inventoryTags';
import { computeFixedMenuPos } from './InventoryTypeaheadField';
import { Icon } from '../../ui';

const NEW_TAG_VALUE = '__new_tag__';

export interface InventoryTagsFieldProps {
    value: Array<InventoryTag>;
    onChange: (tags: Array<InventoryTag>) => void;
    isDisabled?: boolean;
    label?: string;
    labelProps?: FormLabelProps;
    /** Denser inline chips + input for header placement. */
    compact?: boolean;
}

const InventoryTagsField = ({
    value,
    onChange,
    isDisabled = false,
    label = 'Tags',
    labelProps,
    compact = false,
}: InventoryTagsFieldProps) => {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [isCompactInputOpen, setIsCompactInputOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{
        top?: number;
        bottom?: number;
        left: number;
        width: number;
        maxH: number;
    } | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
        return () => clearTimeout(timer);
    }, [query]);

    const { data, isFetching } = useSearchInventoryTags(debouncedQuery, 20, showResults);

    const selectedIds = useMemo(() => new Set(value.map((t) => t._id)), [value]);
    const selectedNames = useMemo(
        () => new Set(value.map((t) => normalizeTagNameKey(t.name)).filter(Boolean)),
        [value],
    );

    const options = useMemo(() => {
        const tags = (data?.tags ?? []).filter(
            (t) =>
                !selectedIds.has(t._id) &&
                !selectedNames.has(normalizeTagNameKey(t.name)),
        );
        const trimmed = query.trim();
        const rows: Array<{ value: string; label: string; isNew?: boolean; newName?: string }> =
            [];
        if (trimmed && !selectedNames.has(normalizeTagNameKey(trimmed))) {
            const exactExisting = (data?.tags ?? []).some(
                (t) => normalizeTagNameKey(t.name) === normalizeTagNameKey(trimmed),
            );
            if (!exactExisting) {
                rows.push({
                    value: NEW_TAG_VALUE,
                    label: trimmed,
                    isNew: true,
                    newName: trimmed,
                });
            }
        }
        for (const t of tags) {
            rows.push({ value: t._id, label: t.name });
        }
        return rows;
    }, [data?.tags, selectedIds, selectedNames, query]);

    const updateMenuPosition = () => {
        const el = inputRef.current;
        if (!el) return;
        setMenuPos(computeFixedMenuPos(el.getBoundingClientRect()));
    };

    useLayoutEffect(() => {
        if (!showResults) {
            setMenuPos(null);
            return;
        }
        updateMenuPosition();
        const onScrollOrResize = () => updateMenuPosition();
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [showResults, options.length]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target)) return;
            if (containerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setShowResults(false);
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    const removeTag = (tagId: string) => {
        onChange(value.filter((t) => t._id !== tagId));
    };

    const selectExisting = (tag: InventoryTag) => {
        const nameKey = normalizeTagNameKey(tag.name);
        if (selectedIds.has(tag._id) || selectedNames.has(nameKey)) return;
        onChange([...value, tag]);
        setQuery('');
        setShowResults(true);
    };

    const handleSelect = (opt: { value: string; label: string; isNew?: boolean }) => {
        if (opt.isNew || opt.value === NEW_TAG_VALUE) {
            const name = query.trim();
            if (!name) return;
            if (!tagListHasName(value, name)) {
                onChange([...value, draftInventoryTag(name)]);
            }
            setQuery('');
            setShowResults(true);
            return;
        }
        const match = data?.tags?.find((t) => t._id === opt.value);
        if (match) selectExisting(match);
    };

    const dropdown =
        showResults && !isDisabled && menuPos ? (
            <Portal>
                <Box
                    ref={menuRef}
                    position="fixed"
                    top={menuPos.top != null ? `${menuPos.top}px` : undefined}
                    bottom={menuPos.bottom != null ? `${menuPos.bottom}px` : undefined}
                    left={`${menuPos.left}px`}
                    width={`${menuPos.width}px`}
                    zIndex={2000}
                >
                    {options.length > 0 ? (
                        <List
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            boxShadow="none"
                            maxH={`${menuPos.maxH}px`}
                            overflowY="auto"
                        >
                            {options.map((opt) => (
                                <ListItem
                                    key={opt.value === NEW_TAG_VALUE ? `${NEW_TAG_VALUE}-${opt.label}` : opt.value}
                                    px={3}
                                    py={2}
                                    fontSize="sm"
                                    fontWeight={opt.isNew ? 'semibold' : 'normal'}
                                    color={opt.isNew ? '#0071E3' : 'inherit'}
                                    cursor="pointer"
                                    _hover={{ bg: 'gray.50' }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelect(opt);
                                    }}
                                >
                                    {opt.isNew ? (
                                        <>
                                            <Text as="span">+ Create new tag: </Text>
                                            <Text as="span" color="gray.500">
                                                &quot;{opt.newName}&quot;
                                            </Text>
                                        </>
                                    ) : (
                                        opt.label
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        !isFetching && (
                            <Box
                                bg="white"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                boxShadow="none"
                                px={3}
                                py={2}
                            >
                                <Text fontSize="sm" color="gray.500">
                                    {query.trim() ? 'No matching tags' : 'Type to search or create a tag'}
                                </Text>
                            </Box>
                        )
                    )}
                </Box>
            </Portal>
        ) : null;

    return (
        <FormControl ref={rootRef}>
            {label ? (
                <FormLabel fontSize="sm" {...labelProps}>
                    {label}
                </FormLabel>
            ) : null}
            {compact ? (
                <Flex ref={containerRef} align="center" gap={2} flexWrap="wrap" minW={0} minH="28px">
                    {value.map((tag) => (
                        <Tag
                            key={tag._id}
                            size="md"
                            borderRadius="md"
                            colorScheme="brand"
                            variant="subtle"
                            flexShrink={0}
                        >
                            <TagLabel>{tag.name}</TagLabel>
                            {!isDisabled && (
                                <TagCloseButton
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => removeTag(tag._id)}
                                />
                            )}
                        </Tag>
                    ))}
                    {isCompactInputOpen || query ? (
                        <Box w="140px" position="relative">
                            <Input
                                ref={inputRef}
                                size="xs"
                                h="28px"
                                px={2}
                                value={query}
                                autoFocus
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setShowResults(true);
                                }}
                                onFocus={() => setShowResults(true)}
                                onBlur={() => {
                                    if (query.trim()) return;
                                    setIsCompactInputOpen(false);
                                    setShowResults(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setQuery('');
                                        setIsCompactInputOpen(false);
                                        setShowResults(false);
                                    }
                                }}
                                placeholder="Search tags…"
                                isDisabled={isDisabled}
                                {...inventoryFieldProps}
                            />
                            {isFetching && showResults && (
                                <Box
                                    position="absolute"
                                    right={2}
                                    top="50%"
                                    transform="translateY(-50%)"
                                >
                                    <Spinner size="xs" />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        !isDisabled && (
                            <Box
                                as="button"
                                type="button"
                                display="inline-flex"
                                alignItems="center"
                                gap={1}
                                fontSize="sm"
                                fontWeight="medium"
                                color="fg.muted"
                                borderRadius="md"
                                px={1}
                                h="28px"
                                _hover={{ color: 'fg.default' }}
                                onClick={() => setIsCompactInputOpen(true)}
                            >
                                <Icon name="plus" size="xs" />
                                Add tag
                            </Box>
                        )
                    )}
                    {dropdown}
                </Flex>
            ) : (
                <>
                    {value.length > 0 && (
                        <Wrap mb={2} spacing={2}>
                            {value.map((tag) => (
                                <WrapItem key={tag._id}>
                                    <Tag
                                        size="sm"
                                        borderRadius="md"
                                        colorScheme="purple"
                                        variant="subtle"
                                    >
                                        <TagLabel>{tag.name}</TagLabel>
                                        {!isDisabled && (
                                            <TagCloseButton
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => removeTag(tag._id)}
                                            />
                                        )}
                                    </Tag>
                                </WrapItem>
                            ))}
                        </Wrap>
                    )}
                    <Box ref={containerRef} position="relative">
                        <Input
                            ref={inputRef}
                            size="sm"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setShowResults(true);
                            }}
                            onFocus={() => setShowResults(true)}
                            placeholder="Search tags…"
                            isDisabled={isDisabled}
                            {...inventoryFieldProps}
                        />
                        {isFetching && showResults && (
                            <Box
                                position="absolute"
                                right={3}
                                top="50%"
                                transform="translateY(-50%)"
                            >
                                <Spinner size="xs" />
                            </Box>
                        )}
                        {dropdown}
                    </Box>
                </>
            )}
        </FormControl>
    );
};

export default InventoryTagsField;
